import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import { ThemeProvider } from './theme/ThemeProvider';
import { SignIn } from './components/Auth/SignIn';
import { SetupGuide } from './components/Auth/SetupGuide';
import { Dashboard } from './components/Dashboard/Dashboard';
import PDFEditor from './components/PDFBuilder/PDFEditor';
import './App.css';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  const requiredVars = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID'
  ];
  
  return requiredVars.every(varName => 
    process.env[varName] && 
    process.env[varName] !== 'your-actual-api-key-here' &&
    process.env[varName] !== 'your-actual-project-id' &&
    process.env[varName] !== 'your-actual-sender-id' &&
    process.env[varName] !== 'your-actual-app-id'
  );
};

function App() {
  // Show setup guide if Firebase is not configured
  if (!isFirebaseConfigured()) {
    return (
      <div className="App">
        <SetupGuide />
      </div>
    );
  }

  return (
    <Provider store={store}>
      <ThemeProvider>
        <AuthProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="App">
              <AppContent />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </Provider>
  );
}

const PDFEditorRoute: React.FC = () => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate('/');
  };
  
  // The PDFEditor manages its own state and gets reportId from URL params
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PDFEditor onBack={handleBack} />
    </div>
  );
};

const PDFViewerRoute: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  
  // Check if this is a shared report from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const isSharedReport = searchParams.get('shared') === 'true';
  
  const handleBack = () => {
    navigate('/');
  };
  
  const handleEditPDF = () => {
    navigate(`/pdf-editor/${reportId}`);
  };
  
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PDFEditor 
        reportId={reportId!} 
        onBack={handleBack}
      />
    </div>
  );
};

const AppContent: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return <SignIn onSuccess={() => {}} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/pdf-viewer/:reportId" element={<PDFViewerRoute />} />
      <Route path="/pdf-editor/:reportId" element={<PDFEditorRoute />} />
    </Routes>
  );
};

export default App;
